import { Button } from 'primereact/button';
import { X } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import type { FC, ReactElement } from 'react';
import type { ExitSlipOutcome } from '../../businessLogicService/StudentDashboardAux/exitSlipOutcome';
import ExitTicketIconPassed from '../../assets/exit-ticket-icon-xs.svg?react';
import ExitTicketIconTaken from '../../assets/exit-ticket-icon-taken-xs.svg?react';
import FaceProud from '../../assets/exit-ticket-inferred-face-proud.svg?react';
import InferredSkillExclamation from './DashboardIcons/InferredSkillExclamation';
import Check from '../../assets/Check.svg?react';
import {
  EXIT_SLIP_BANNER_LAYOUT_ID,
  EXIT_SLIP_BANNER_RADIUS,
  EXIT_SLIP_BANNER_TRANSITION,
} from './exitSlipBannerMotion';

interface Props {
  outcome: ExitSlipOutcome;
  // Points earned over points available (0..1), shown as a whole percentage next to the status word.
  scoreRatio: number;
  // Sends the student to the skill the exit ticket's lesson covered: scrolls to its unit-path card, or
  // opens the skill's own page when that card isn't on the path. Null when the lesson carries no skill
  // at all, which drops the call to action — there is nowhere to send them.
  onGoToSkill: (() => void) | null;
  // The student closed the banner: it must stay closed until the next exit ticket.
  onDismiss: () => void;
  // True when the full-width banner was on screen a render ago, i.e. the card is arriving from the
  // collapse morph rather than mounting cold on a page load. Only then does its content wait.
  arrivingFromBanner: boolean;
  // The skill the ticket's lesson covered. Named on the card only in the 'passed-and-demonstrated'
  // state, where the skill the student has just earned is the whole point of the message.
  skillName?: string | null;
}

interface OutcomeCopy {
  statusLabel: string;
  // 'Taken' reads neutral (white); every passing state takes the green the result modal uses.
  statusClassName: string;
  statusIcon: ReactElement;
  callToAction: string;
}

// The four designed states. Only the status word, its color/icon and the call to action differ, so the
// banner itself is one layout driven by this table.
const outcomeCopy: Record<ExitSlipOutcome, OutcomeCopy> = {
  taken: {
    statusLabel: 'Taken',
    statusClassName: 'tw-text-white',
    statusIcon: <ExitTicketIconTaken />,
    callToAction: 'Practice skill',
  },
  passed: {
    statusLabel: 'Passed',
    statusClassName: 'tw-text-green-300',
    statusIcon: <ExitTicketIconPassed />,
    callToAction: 'Practice skill',
  },
  'on-fire': {
    statusLabel: 'Passed',
    statusClassName: 'tw-text-green-300',
    statusIcon: <ExitTicketIconPassed />,
    callToAction: 'Try Challenge',
  },
  'passed-and-demonstrated': {
    statusLabel: 'Passed',
    statusClassName: 'tw-text-green-300',
    statusIcon: <ExitTicketIconPassed />,
    callToAction: '+ skill done!',
  },
};

/**
 * What today's exit ticket collapses into once the student has finished it: a small card pinned to the
 * bottom-right corner showing how they did and linking back to the lesson's skill on the unit path.
 * Replaces the full-width DashboardExitSlipBanner, which only invites the student to take the ticket.
 *
 * Lifecycle (owned by DashboardExitSlipQuery): it lives for the rest of the day, disappears for good
 * once the student closes it, and returns with the next exit ticket.
 *
 * Arriving from a finished ticket it is not mounted so much as morphed into — see
 * EXIT_SLIP_BANNER_LAYOUT_ID for how the wide banner shrinks and travels into this card.
 */
export const DashboardExitSlipCollapsedBanner: FC<Props> = ({
  outcome,
  scoreRatio,
  onGoToSkill,
  onDismiss,
  arrivingFromBanner,
  skillName,
}) => {
  const { statusLabel, statusClassName, statusIcon, callToAction } = outcomeCopy[outcome];
  const reduceMotion = useReducedMotion();
  const morphs = arrivingFromBanner && !reduceMotion;
  // Earning a skill outranks the score: the card stops reporting how the ticket went and reports what
  // it won instead. Needs a skill to name and somewhere to send them, so it falls back to the ordinary
  // read-out when the lesson carries no skill.
  const demonstrated = outcome === 'passed-and-demonstrated' && !!skillName && !!onGoToSkill;

  return (
    <motion.div
      layoutId={EXIT_SLIP_BANNER_LAYOUT_ID}
      transition={reduceMotion ? { duration: 0 } : EXIT_SLIP_BANNER_TRANSITION}
      style={{ borderRadius: EXIT_SLIP_BANNER_RADIUS }}
      className={`nc_exit-slip-colapsed-banner lg:tw-right-8 ${
        demonstrated ? 'nc_exit-slip-colapsed-banner--skill-earned' : ''
      }`}
      role="status"
    >
      {/* Held back while the box is still travelling: the morph drives the card with a scale
          transform, which would visibly squash this text on the way down. Fading it in as the card
          lands reads as the banner collapsing into an empty card that then fills with the result.
          Opacity leaves it in the accessibility tree throughout, so the live region still announces
          the score immediately. */}
      <motion.div
        initial={{ opacity: morphs ? 0 : 1 }}
        animate={{ opacity: 1 }}
        transition={morphs ? { duration: 0.2, delay: 0.25 } : { duration: 0 }}
      >
        {/* The navy panel. In the skill-earned state the card's own padding moves in here, so the
            light band below can run to the card's edges. */}
        <div className={demonstrated ? 'tw-px-4 tw-pb-4 tw-pt-4' : ''}>
          <div className="tw-flex tw-justify-between tw-gap-2">
            <h3 className="tw-m-0 tw-font-atkinson tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-white">
              Today’s exit ticket!
            </h3>
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss today’s exit ticket result"
              className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-leading-none tw-text-white"
            >
              <X className="tw-h-4 tw-w-4" aria-hidden="true" />
            </button>
          </div>

          {/* How the ticket itself went. Shown in every state, the skill-earned one included: winning
              the skill is the headline, but the score is still what the student asked about. */}
          <div className="tw-mt-3 tw-flex tw-items-center tw-justify-center tw-gap-2">
            <span className={`tw-font-atkinson tw-text-lg tw-font-bold ${statusClassName}`}>{statusLabel}</span>
            <span className="tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-[3px] tw-bg-blue-50">
              {statusIcon}
            </span>
            <span className="tw-font-atkinson tw-text-sm tw-font-bold tw-text-white">
              {Math.round(scoreRatio * 100)}%
            </span>
          </div>
        </div>

        {demonstrated ? (
          /* The second band: what the ticket won, on its own light ground. */
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-bg-indigo-350 tw-px-4 tw-pb-4 tw-pt-2">
            {/* One row, as designed. The face is pulled up by half its height so the band's top edge
                runs across its middle — the card clips it (see the --skill-earned modifier), which is
                what lets it sit on the seam rather than float below it. */}
            <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1.5">
              <Check className="tw-w-4 tw-h-4" />
              <span className="tw-whitespace-nowrap tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-[#1D1B18]">
                {callToAction}
              </span>
              <FaceProud className="tw--mt-7 tw-h-14 tw-w-14 tw-shrink-0" aria-hidden="true" />
              <InferredSkillExclamation className="tw--mt-4 tw-shrink-0" />
            </div>

            {/* The skill's own name, and the way through to it. A button rather than a link because
                the destination is reached through the router (see onGoToSkill). Held to one line: a
                long skill name has to give way to an ellipsis rather than grow the card. */}
            <button
              type="button"
              onClick={onGoToSkill}
              title={skillName ?? undefined}
              className="tw-block tw-w-full tw-truncate tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-center tw-font-atkinson tw-text-sm tw-font-bold tw-leading-5 tw-text-[#1D1B18] tw-underline tw-decoration-2 tw-underline-offset-4"
            >
              {skillName}
            </button>
          </div>
        ) : (
          onGoToSkill && (
            <Button label={callToAction} onClick={onGoToSkill} className="!tw-mt-3 !tw-w-full !tw-justify-center" />
          )
        )}
      </motion.div>
    </motion.div>
  );
};

export default DashboardExitSlipCollapsedBanner;
